import React, {useState, useEffect} from 'react';
import {useParams, useNavigate} from 'react-router';
import {
    Container,
    Typography,
    Box,
    CircularProgress,
    Alert,
    Breadcrumbs,
    Link,
    Paper,
    Stack,
    Chip,
    Divider,
    Button,
    Grid,
    Tab,
    Tabs,
} from '@mui/material';
import {
    Home as HomeIcon,
    School as SchoolIcon,
    Topic as TopicIcon,
    Assignment as AssignmentIcon,
    Edit as EditIcon,
    AutoFixHigh as AutoFixHighIcon,
    Quiz as QuizIcon,
} from '@mui/icons-material';
import {createApiClient} from '../../services/ApiClient.jsx';
import TopicForm from './TopicForm.jsx';
import AIChatInterface from './AIChatInterface.jsx';
import UpdatePopup from '../../components/UpdatePopup.jsx';

const TeacherTopicDashboard = ({accessToken, claims}) => {
    const {courseId, topicId} = useParams();
    const navigate = useNavigate();

    // Course and topic state
    const [course, setCourse] = useState(null);
    const [topic, setTopic] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [tabValue, setTabValue] = useState(0);

    // Edit popup state
    const [showEditTopic, setShowEditTopic] = useState(false);

    // Form-related state (lifted from TopicForm)
    const [formData, setFormData] = useState([]);
    const [formLoading, setFormLoading] = useState(false);
    const [formError, setFormError] = useState(null);
    const [needsFormCreation, setNeedsFormCreation] = useState(false);
    const [isAutoCreating, setIsAutoCreating] = useState(false);

    const apiClient = createApiClient(accessToken);

    //const config = {
    //        tex: {
    //            inlineMath: [['$', '$'], ['\\(', '\\)']],
    //            displayMath: [['$$', '$$'], ['\\[', '\\]']]
     //       }
    //    };
    // Course fetching methods
    const fetchCourse = async () => {
        try {
            const courseResponse = await apiClient.get(`/api/courses/${courseId}`);
            setCourse(courseResponse);
            setError(null);
        } catch (err) {
            console.error('Error fetching course:', err);
            setError('Failed to load course. Please try again later.');
        }
    };

    const fetchTopic = async () => {
        try {
            const topicsResponse = await apiClient.get(`/api/courses/${courseId}/topics`);
            const foundTopic = topicsResponse.topics.find(t => t.id === topicId);

            if (!foundTopic) {
                throw new Error('Topic not found');
            }

            setTopic(foundTopic);
            setError(null);
        } catch (err) {
            console.error('Error fetching topic:', err);
            setError('Failed to load topic. Please try again later.');
        }
    };

    // Form management methods (moved from TopicForm)
    const fetchForm = async () => {
        setFormLoading(true);
        setIsAutoCreating(false);
        try {
            const data = await apiClient.get(`/api/courses/${courseId}/topics/${topicId}/form`);
            setFormData(data || []);
            setNeedsFormCreation(false);
            setFormError(null);
            setFormLoading(false);
        } catch (err) {
            console.error('Form fetch error:', err);

            const errorMessage = err.message || err.toString() || '';
            const isNoFormError = errorMessage.includes('No form found for this topic');

            const status =
                (err.response && err.response.status) ||
                err.status ||
                (typeof err.toString === 'function' && err.toString().includes('404'));

            if (isNoFormError) {
                console.log('No form found error detected, automatically creating form');
                setFormError(null);
                setNeedsFormCreation(false);
                setIsAutoCreating(true);
                try {
                    await createNewForm();
                    return;
                } catch (createErr) {
                    console.error('Auto-creation failed:', createErr);
                    setFormError('Failed to automatically create form');
                    setFormLoading(false);
                    setIsAutoCreating(false);
                    return;
                }
            }

            if (status === 404 || (err.message && err.message.includes('404'))) {
                console.log('404 detected, showing create form button');
                setNeedsFormCreation(true);
                setFormError(null);
            } else {
                setFormError('Failed to load form data');
                setNeedsFormCreation(false);
            }
            setFormLoading(false);
        }
    };

    const createNewForm = async () => {
        try {
            await apiClient.post('/api/forms/', {
                topic_id: topicId
            });
            await fetchForm();
            await fetchTopic(); // Update topic stats
        } catch (err) {
            console.error(err);
            setFormError('Failed to create new form');
            throw err;
        }
    };

    const saveForm = async (formDataToSave) => {
        console.log('Form saved:', formDataToSave);
        try {
            await apiClient.put(`/api/forms/${formDataToSave.id}`, formDataToSave);
            setFormData(formDataToSave); // Update local state
            await fetchTopic(); // Update topic stats
            return formDataToSave;
        } catch (err) {
            console.error('Error saving form:', err);
            // Don't set formError here - it would hide the entire form
            // Let the FormBuilder handle the error display via snackbar
            // Extract error message from backend response if available
            const errorMessage = err.response?.data?.detail ||
                                err.response?.data?.message ||
                                err.message ||
                                'Failed to save form data';
            // Create a new error with the backend message
            const error = new Error(errorMessage);
            error.response = err.response;
            throw error;
        }
    };

    // Method for AI Chat or other components to update form data
    const updateFormData = async (newFormData) => {
        try {
            const updatedData = await saveForm(newFormData);
            setFormData(updatedData);
            return updatedData;
        } catch (err) {
            console.error('Failed to update form data:', err);
            throw err;
        }
    };

    // Method for AI Chat or other components to refresh form data
    const refreshFormData = async () => {
        await fetchForm();
    };

    // Edit topic handlers
    const handleEditTopic = () => {
        setShowEditTopic(true);
    };

    const handleCloseEditTopic = () => {
        setShowEditTopic(false);
    };

    const handleTopicUpdate = async (updatedTopic) => {
        // Update the local topic state with the updated data
        if (updatedTopic) {
            setTopic(updatedTopic);
        } else {
            // Fallback: refetch the topic data
            await fetchTopic();
        }
    };

    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true);
                await Promise.all([fetchCourse(), fetchTopic()]);

                // After topic is loaded, fetch form data
                if (topicId && courseId) {
                    setFormLoading(true);
                    setFormError(null);
                    setNeedsFormCreation(false);
                    setIsAutoCreating(false);

                    try {
                        await fetchForm();
                    } catch (err) {
                        console.error('Error fetching form:', err);
                    } finally {
                        setFormLoading(false);
                    }
                }
            } catch (err) {
                console.error('Error fetching topic dashboard data:', err);
                setError('Failed to load topic dashboard. Please try again later.');
            } finally {
                setLoading(false);
            }
        };

        if (accessToken && courseId && topicId) fetchData();
    }, [accessToken, courseId, topicId]);

    const handleTabChange = (event, newValue) => {
        setTabValue(newValue);
    };

    if (loading) {
        return (
            <Container maxWidth="xl" sx={{py: 4}}>
                <Box sx={{display: 'flex', justifyContent: 'center', my: 4}}>
                    <CircularProgress/>
                </Box>
            </Container>
        );
    }

    if (error) {
        return (
            <Container maxWidth="xl" sx={{py: 4}}>
                <Alert severity="error" sx={{mb: 4}}>
                    {error}
                </Alert>
            </Container>
        );
    }

    if (!course || !topic) {
        return (
            <Container maxWidth="xl" sx={{py: 4}}>
                <Alert severity="warning" sx={{mb: 4}}>
                    {!course ? 'Course not found.' : 'Topic not found.'}
                </Alert>
            </Container>
        );
    }

    return (
        <Container maxWidth="xl" sx={{py: 2}}>
            {/* Breadcrumbs */}
            <Breadcrumbs aria-label="breadcrumb" sx={{mb: 2}}>
                <Link
                    color="inherit"
                    href="/teacher/dashboard"
                    sx={{display: 'flex', alignItems: 'center'}}
                >
                    <HomeIcon sx={{mr: 0.5}} fontSize="inherit"/>
                    Dashboard
                </Link>
                <Link
                    color="inherit"
                    href={`/teacher/course/${courseId}`}
                    sx={{display: 'flex', alignItems: 'center'}}
                >
                    <SchoolIcon sx={{mr: 0.5}} fontSize="inherit"/>
                    {course.title}
                </Link>
                <Typography
                    color="text.primary"
                    sx={{display: 'flex', alignItems: 'center'}}
                >
                    <TopicIcon sx={{mr: 0.5}} fontSize="inherit"/>
                    {topic.name}
                </Typography>
            </Breadcrumbs>

            {/* Divider between breadcrumbs and header */}
            <Divider sx={{mb: 3}}/>

            {/* Topic Header with Title/Edit + Description/Stats */}
            <Box sx={{mb: 3}}>
                {/* Course context + Topic title + Edit button */}
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{mb: 1}}>
                    <Box>
                        <Typography variant="h6" color="text.secondary" sx={{fontSize: '0.9rem', mb: 0.5}}>
                            {course.title}
                        </Typography>
                        <Typography variant="h4">{topic.name}</Typography>
                    </Box>
                    <Button
                        variant="outlined"
                        startIcon={<EditIcon/>}
                        size="small"
                        onClick={handleEditTopic}
                    >
                        Edit
                    </Button>
                </Stack>

                {/* Description on left, stats chips on right */}
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                    {topic.description ? (
                        <Typography variant="subtitle1" color="text.secondary">
                            {topic.description}
                        </Typography>
                    ) : <Box/>}

                    <Stack direction="row" spacing={1}>
                        <Chip
                            icon={<QuizIcon/>}
                            label={topic.question_count === 0 ? 'No Questions' : `${topic.question_count} Question${topic.question_count === 1 ? '' : 's'}`}
                            variant="outlined"
                            size="small"
                            color={topic.question_count === 0 ? 'warning' : 'success'}
                        />
                        <Chip
                            label={topic.is_published ? 'Published' : 'Draft'}
                            size="small"
                            color={topic.is_published ? 'success' : 'default'}
                            variant="outlined"
                        />
                    </Stack>
                </Stack>
            </Box>

            {/* Tabs for different sections */}
            <Box sx={{borderBottom: 1, borderColor: 'divider', mb: 2}}>
                <Tabs value={tabValue} onChange={handleTabChange} aria-label="topic sections">
                    <Tab
                        label="Questions"
                        icon={<AssignmentIcon/>}
                        iconPosition="start"
                    />
                    <Tab
                        label="AI Assistant"
                        icon={<AutoFixHighIcon/>}
                        iconPosition="start"
                        disabled={topic?.is_published}
                    />
                </Tabs>
            </Box>

            {/* Tab Content */}
            <Box sx={{minHeight: 0}}>
                {tabValue === 0 && (
                    <TopicForm
                        // Form data and state
                        formData={formData}
                        formLoading={formLoading}
                        formError={formError}
                        needsFormCreation={needsFormCreation}
                        isAutoCreating={isAutoCreating}
                        // Form methods
                        onCreateForm={createNewForm}
                        onSaveForm={saveForm}
                        // For AI Chat integration
                        onUpdateFormData={updateFormData}
                        onRefreshFormData={refreshFormData}
                        // Topic published status
                        isPublished={topic?.is_published}
                    />
                )}
                {tabValue === 1 && (
                    <AIChatInterface
                        formData={formData}
                        onUpdateFormData={updateFormData}
                        onRefreshFormData={refreshFormData}
                        accessToken={accessToken}
                        courseId={courseId}
                        topicId={topicId}
                    />
                )}
            </Box>

            {/* Edit Topic Popup */}
            <UpdatePopup
                open={showEditTopic}
                onClose={handleCloseEditTopic}
                onUpdate={handleTopicUpdate}
                entityType="topic"
                entityData={topic}
                apiClient={apiClient}
                courseId={courseId}
                topicId={topicId}
            />
        </Container>
    );
};

export default TeacherTopicDashboard;